import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Phone,
  CheckCircle,
  Star,
  Shield,
  Award,
  Clock,
  Droplets,
  Home,
  Car,
  Building2,
  Layers,
  Sparkles,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Users,
  ThumbsUp,
  MapPin
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function HomePage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    property_type: "",
    additional_info: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError("");
    
    try {
      await axios.post(`${API}/quote`, formData);
      setSubmitSuccess(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        service_type: "",
        property_type: "",
        additional_info: ""
      });
    } catch (error) {
      setSubmitError("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    {
      icon: Home,
      title: "House Washing",
      description: "Restore your home's exterior with our gentle yet effective house washing service.",
      link: "/services#house-washing"
    },
    {
      icon: Car,
      title: "Driveway Cleaning",
      description: "Remove oil stains, dirt, and grime from your driveway and garage floors.",
      link: "/services#driveway-cleaning"
    },
    {
      icon: Layers,
      title: "Deck & Patio Cleaning",
      description: "Revitalize your outdoor living spaces with professional deck and patio cleaning.",
      link: "/services#deck-patio"
    },
    {
      icon: Sparkles,
      title: "Roof Cleaning",
      description: "Safely remove moss, algae, and debris from your roof without damage.",
      link: "/services#roof-cleaning"
    },
    {
      icon: Building2,
      title: "Commercial Washing",
      description: "Keep your business looking professional with our commercial pressure washing.",
      link: "/services#commercial"
    },
    {
      icon: MapPin,
      title: "Sidewalk Cleaning",
      description: "Make your walkways safe and attractive with thorough sidewalk cleaning.",
      link: "/services#sidewalk"
    }
  ];

  const testimonials = [
    {
      name: "Michael R.",
      location: "Manhattan, NY",
      rating: 5,
      text: "BuyVistas transformed my home's exterior! The siding looks brand new, and they were incredibly professional. Highly recommend!"
    },
    {
      name: "Sarah L.",
      location: "Brooklyn, NY",
      rating: 5,
      text: "Outstanding service! They cleaned our driveway and patio, and the results were amazing. Fair pricing and excellent customer service."
    },
    {
      name: "David K.",
      location: "Queens, NY",
      rating: 5,
      text: "We use BuyVistas for our commercial property monthly. Consistent, reliable, and always professional. The best in the business!"
    }
  ];

  const faqs = [
    {
      question: "What is pressure washing and how does it work?",
      answer: "Pressure washing uses high-pressure water spray to remove dirt, grime, mold, algae, and other contaminants from surfaces. Our professional equipment adjusts pressure levels based on the surface being cleaned to ensure effective cleaning without damage."
    },
    {
      question: "How often should I have my property pressure washed?",
      answer: "For most residential properties, we recommend annual pressure washing. However, high-traffic areas like driveways may benefit from bi-annual cleaning. Commercial properties often need monthly or quarterly service depending on foot traffic and location."
    },
    {
      question: "Is pressure washing safe for all surfaces?",
      answer: "Different surfaces require different pressure levels and techniques. Our trained technicians assess each surface and use appropriate pressure settings, soft washing for delicate surfaces, and standard pressure washing for harder surfaces like concrete."
    },
    {
      question: "How much does pressure washing cost?",
      answer: "Pricing depends on the size of the area, type of surface, and level of cleaning needed. We offer free quotes and competitive pricing. Contact us for a personalized estimate based on your specific needs."
    },
    {
      question: "Do you offer any guarantees?",
      answer: "Yes! We stand behind our work with a 100% satisfaction guarantee. If you're not completely happy with our service, we'll re-clean the area at no additional cost."
    },
    {
      question: "What areas do you serve?",
      answer: "We provide pressure washing services throughout the New York metropolitan area, including Manhattan, Brooklyn, Queens, Bronx, Staten Island, and surrounding areas. Contact us to confirm service availability in your location."
    }
  ];

  const stats = [
    { number: "15+", label: "Years Experience", icon: Award },
    { number: "5000+", label: "Happy Customers", icon: Users },
    { number: "100%", label: "Satisfaction Rate", icon: ThumbsUp },
    { number: "24/7", label: "Customer Support", icon: Clock }
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=2070')`,
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block text-sky-400 font-bold uppercase tracking-widest text-sm mb-4">
                Professional Cleaning Services
              </span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="heading-primary text-white mb-6"
              data-testid="hero-headline"
            >
              Professional Pressure Washing Services for{" "}
              <span className="text-gradient">Homes & Businesses</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-slate-300 mb-8 leading-relaxed"
              data-testid="hero-subheadline"
            >
              Restore the beauty of your property with our expert pressure washing solutions. 
              Trusted by thousands of homeowners and businesses across New York.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <Link to="/contact" className="btn-primary" data-testid="hero-quote-btn">
                <span>Get Free Quote</span>
              </Link>
              <a href="tel:+1-888-435-4674" className="btn-secondary" data-testid="hero-call-btn">
                <span className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Call Now
                </span>
              </a>
            </motion.div>
            
            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap gap-6 mt-12"
            >
              <div className="flex items-center gap-2 text-white/80">
                <Shield className="w-5 h-5 text-sky-400" />
                <span className="text-sm">Fully Insured</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <CheckCircle className="w-5 h-5 text-sky-400" />
                <span className="text-sm">Licensed Professionals</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Award className="w-5 h-5 text-sky-400" />
                <span className="text-sm">Satisfaction Guaranteed</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-slate-900 py-12" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <stat.icon className="w-8 h-8 text-sky-500 mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-extrabold text-white mb-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {stat.number}
                </div>
                <div className="text-slate-400 text-sm uppercase tracking-wider">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding bg-white" data-testid="about-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sky-600 font-bold uppercase tracking-widest text-sm mb-4 block">
                About BuyVistas
              </span>
              <h2 className="heading-secondary text-slate-900 mb-6">
                New York's Trusted Pressure Washing Experts
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                With over 15 years of experience, BuyVistas has become the go-to pressure washing 
                service for homes and businesses across New York. Our team of certified professionals 
                uses state-of-the-art equipment and eco-friendly cleaning solutions to deliver 
                exceptional results every time.
              </p>
              <p className="text-slate-600 mb-8 leading-relaxed">
                We understand that your property is your biggest investment. That's why we treat 
                every project with the care and attention it deserves, ensuring your complete 
                satisfaction with our 100% guarantee.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                {["Licensed & Insured", "Eco-Friendly Products", "Free Estimates", "Same-Day Service"].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-sky-600 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
              
              <Link to="/about" className="inline-flex items-center gap-2 text-sky-600 font-bold uppercase tracking-wider hover:gap-4 transition-all" data-testid="about-learn-more">
                Learn More About Us
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1552197717-fdea1c70c5c7?q=80&w=2070"
                  alt="Professional pressure washing team"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute -bottom-6 -left-6 bg-sky-600 text-white p-6">
                  <div className="text-4xl font-extrabold mb-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>15+</div>
                  <div className="text-sm uppercase tracking-wider">Years of Excellence</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-slate-50" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sky-600 font-bold uppercase tracking-widest text-sm mb-4 block">
              Our Services
            </span>
            <h2 className="heading-secondary text-slate-900 mb-6">
              Comprehensive Pressure Washing Solutions
            </h2>
            <p className="text-slate-600">
              From residential homes to commercial properties, we offer a full range of pressure 
              washing services to meet all your cleaning needs.
            </p>
          </div>
          
          <motion.div 
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                variants={fadeInUp}
                className="service-card group relative overflow-hidden bg-white border border-slate-200 p-8 hover:border-sky-500 transition-all duration-300"
                data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-14 h-14 bg-sky-100 flex items-center justify-center mb-6 group-hover:bg-sky-600 transition-colors">
                  <service.icon className="w-7 h-7 text-sky-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 uppercase tracking-wide" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {service.title}
                </h3>
                <p className="text-slate-600 mb-4">
                  {service.description}
                </p>
                <Link 
                  to={service.link}
                  className="inline-flex items-center gap-2 text-sky-600 font-semibold hover:gap-3 transition-all"
                >
                  Learn More
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </motion.div>
          
          <div className="text-center mt-12">
            <Link to="/services" className="btn-primary" data-testid="view-all-services-btn">
              <span>View All Services</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding bg-white" data-testid="why-choose-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=800"
                  alt="Before cleaning"
                  className="w-full h-60 object-cover"
                />
                <img 
                  src="https://images.unsplash.com/photo-1759004673924-da9bd63dbb16?q=80&w=800"
                  alt="After cleaning"
                  className="w-full h-60 object-cover"
                />
                <img 
                  src="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?q=80&w=800"
                  alt="Clean house exterior"
                  className="w-full h-60 object-cover col-span-2"
                />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <span className="text-sky-600 font-bold uppercase tracking-widest text-sm mb-4 block">
                Why Choose Us
              </span>
              <h2 className="heading-secondary text-slate-900 mb-6">
                The BuyVistas Difference
              </h2>
              
              <div className="space-y-6">
                {[
                  {
                    icon: Shield,
                    title: "Fully Licensed & Insured",
                    description: "Complete peace of mind with our comprehensive coverage and professional certifications."
                  },
                  {
                    icon: Award,
                    title: "100% Satisfaction Guarantee",
                    description: "Not happy with our work? We'll re-clean the area at no additional cost."
                  },
                  {
                    icon: Droplets,
                    title: "Eco-Friendly Solutions",
                    description: "Safe for your family, pets, and the environment without compromising on cleaning power."
                  },
                  {
                    icon: Clock,
                    title: "Flexible Scheduling",
                    description: "We work around your schedule with same-day and weekend appointments available."
                  }
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="w-12 h-12 bg-sky-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-sky-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">{item.title}</h4>
                      <p className="text-slate-600 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="section-padding bg-slate-900" data-testid="before-after-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sky-400 font-bold uppercase tracking-widest text-sm mb-4 block">
              Our Results
            </span>
            <h2 className="heading-secondary text-white mb-6">
              See the Transformation
            </h2>
            <p className="text-slate-400">
              Our professional pressure washing services deliver dramatic results. 
              Check out these before and after transformations.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { before: "Dirty, stained driveway", after: "Clean, restored concrete", type: "Driveway" },
              { before: "Algae-covered siding", after: "Fresh, clean exterior", type: "House Washing" },
              { before: "Weathered deck surface", after: "Revitalized wood deck", type: "Deck Cleaning" }
            ].map((item, index) => (
              <motion.div
                key={item.type}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-800 overflow-hidden"
              >
                <div className="grid grid-cols-2">
                  <div className="relative">
                    <img 
                      src="https://images.unsplash.com/photo-1766823711275-91b53e6cebb5?q=80&w=400"
                      alt="Before"
                      className="w-full h-48 object-cover opacity-70"
                    />
                    <span className="absolute bottom-2 left-2 bg-red-600 text-white text-xs font-bold px-2 py-1">BEFORE</span>
                  </div>
                  <div className="relative">
                    <img 
                      src="https://images.unsplash.com/photo-1759004673924-da9bd63dbb16?q=80&w=400"
                      alt="After"
                      className="w-full h-48 object-cover"
                    />
                    <span className="absolute bottom-2 left-2 bg-green-600 text-white text-xs font-bold px-2 py-1">AFTER</span>
                  </div>
                </div>
                <div className="p-6">
                  <h4 className="text-white font-bold text-lg mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    {item.type}
                  </h4>
                  <p className="text-slate-400 text-sm">
                    {item.before} → {item.after}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-slate-50" data-testid="testimonials-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sky-600 font-bold uppercase tracking-widest text-sm mb-4 block">
              Testimonials
            </span>
            <h2 className="heading-secondary text-slate-900 mb-6">
              What Our Customers Say
            </h2>
            <p className="text-slate-600">
              Don't just take our word for it. Here's what our satisfied customers have to say about our services.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="testimonial-card bg-white p-8 border-l-4 border-sky-500 relative"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-600 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-bold text-slate-900">{testimonial.name}</p>
                  <p className="text-slate-500 text-sm">{testimonial.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="section-padding bg-white" data-testid="service-areas-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sky-600 font-bold uppercase tracking-widest text-sm mb-4 block">
                Service Areas
              </span>
              <h2 className="heading-secondary text-slate-900 mb-6">
                Serving the Greater New York Area
              </h2>
              <p className="text-slate-600 mb-8">
                We proudly serve residential and commercial customers throughout the New York metropolitan area. 
                Our service coverage includes:
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                {["Manhattan", "Brooklyn", "Queens", "Bronx", "Staten Island", "Long Island", "Westchester", "New Jersey"].map((area) => (
                  <div key={area} className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-sky-600" />
                    <span className="text-slate-700">{area}</span>
                  </div>
                ))}
              </div>
              
              <Link to="/service-areas" className="inline-flex items-center gap-2 text-sky-600 font-bold uppercase tracking-wider hover:gap-4 transition-all" data-testid="view-service-areas">
                View All Service Areas
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            
            <div className="h-[400px] bg-slate-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d193595.15830869428!2d-74.11976397304605!3d40.69766374874431!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24fa5d33f083b%3A0xc80b8f06e177fe62!2sNew%20York%2C%20NY!5e0!3m2!1sen!2sus!4v1703000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="BuyVistas Service Area Map"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="section-padding bg-slate-50" data-testid="pricing-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sky-600 font-bold uppercase tracking-widest text-sm mb-4 block">
              Pricing
            </span>
            <h2 className="heading-secondary text-slate-900 mb-6">
              Transparent & Competitive Pricing
            </h2>
            <p className="text-slate-600">
              We believe in honest, upfront pricing. Contact us for a free, no-obligation quote tailored to your specific needs.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Residential",
                price: "Starting at $149",
                features: ["House Exterior Washing", "Driveway Cleaning", "Deck & Patio Cleaning", "Fence Cleaning", "Free Estimates"]
              },
              {
                title: "Premium",
                price: "Starting at $299",
                features: ["Everything in Residential", "Roof Soft Washing", "Gutter Cleaning", "Window Cleaning", "Priority Scheduling"],
                popular: true
              },
              {
                title: "Commercial",
                price: "Custom Quote",
                features: ["Building Exterior", "Parking Lot Cleaning", "Sidewalk Cleaning", "Graffiti Removal", "Scheduled Maintenance"]
              }
            ].map((plan, index) => (
              <motion.div
                key={plan.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative p-8 ${plan.popular ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200'}`}
                data-testid={`pricing-${plan.title.toLowerCase()}`}
              >
                {plan.popular && (
                  <span className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-orange-500 text-white text-xs font-bold px-4 py-1 uppercase">
                    Most Popular
                  </span>
                )}
                <h3 className="text-xl font-bold uppercase tracking-wide mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {plan.title}
                </h3>
                <div className="text-3xl font-extrabold mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {plan.price}
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2">
                      <CheckCircle className={`w-5 h-5 ${plan.popular ? 'text-sky-400' : 'text-sky-600'}`} />
                      <span className={plan.popular ? 'text-slate-300' : 'text-slate-600'}>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link 
                  to="/contact"
                  className={`block text-center py-3 font-bold uppercase tracking-wider transition-colors ${
                    plan.popular 
                      ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                      : 'bg-slate-900 hover:bg-slate-800 text-white'
                  }`}
                >
                  Get Quote
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-white" data-testid="faq-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-sky-600 font-bold uppercase tracking-widest text-sm mb-4 block">
                FAQ
              </span>
              <h2 className="heading-secondary text-slate-900 mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-slate-600">
                Have questions? We've got answers. If you don't find what you're looking for, feel free to contact us.
              </p>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="border border-slate-200 px-6"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-sky-600 py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section-padding bg-slate-50" data-testid="contact-form-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <span className="text-sky-600 font-bold uppercase tracking-widest text-sm mb-4 block">
                Get In Touch
              </span>
              <h2 className="heading-secondary text-slate-900 mb-6">
                Request Your Free Quote Today
              </h2>
              <p className="text-slate-600 mb-8">
                Ready to transform your property? Fill out the form and one of our experts will get back to you within 24 hours with a customized quote.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-sky-100 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-sky-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Call Us</p>
                    <a href="tel:+1-888-435-4674" className="font-bold text-slate-900 hover:text-sky-600 transition-colors">
                      +1-888-435-4674
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-sky-100 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-sky-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Business Hours</p>
                    <p className="font-bold text-slate-900">Mon - Sat: 7AM - 7PM</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 border border-slate-200">
              {submitSuccess ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Thank You!</h3>
                  <p className="text-slate-600">We've received your request and will contact you within 24 hours.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Name *</label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your name"
                        required
                        className="rounded-none"
                        data-testid="quote-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Email *</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your@email.com"
                        required
                        className="rounded-none"
                        data-testid="quote-email-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Phone *</label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(123) 456-7890"
                        required
                        className="rounded-none"
                        data-testid="quote-phone-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Service Type *</label>
                      <Select onValueChange={(value) => handleSelectChange("service_type", value)} required>
                        <SelectTrigger className="rounded-none" data-testid="quote-service-select">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="house-washing">House Washing</SelectItem>
                          <SelectItem value="driveway-cleaning">Driveway Cleaning</SelectItem>
                          <SelectItem value="deck-patio">Deck & Patio Cleaning</SelectItem>
                          <SelectItem value="roof-cleaning">Roof Cleaning</SelectItem>
                          <SelectItem value="commercial">Commercial Washing</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Property Type</label>
                    <Select onValueChange={(value) => handleSelectChange("property_type", value)}>
                      <SelectTrigger className="rounded-none" data-testid="quote-property-select">
                        <SelectValue placeholder="Select property type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residential">Residential</SelectItem>
                        <SelectItem value="commercial">Commercial</SelectItem>
                        <SelectItem value="industrial">Industrial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Additional Information</label>
                    <Textarea
                      name="additional_info"
                      value={formData.additional_info}
                      onChange={handleInputChange}
                      placeholder="Tell us more about your project..."
                      rows={4}
                      className="rounded-none"
                      data-testid="quote-message-input"
                    />
                  </div>
                  
                  {submitError && (
                    <p className="text-red-600 text-sm">{submitError}</p>
                  )}
                  
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 uppercase tracking-wider rounded-none"
                    data-testid="quote-submit-btn"
                  >
                    {isSubmitting ? "Submitting..." : "Get Free Quote"}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-gradient py-16" data-testid="cta-section">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="heading-secondary text-white mb-4">
                Ready to Transform Your Property?
              </h2>
              <p className="text-slate-300 text-lg">
                Get your free quote today and see the BuyVistas difference.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-testid="cta-quote-btn">
                <span>Get Free Quote</span>
              </Link>
              <a href="tel:+1-888-435-4674" className="btn-secondary" data-testid="cta-call-btn">
                <span className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  +1-888-435-4674
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
